import React from "react";
import { useState, useEffect } from "react";
import CompareMatrixPricing from "../../CompareMatrixPricing/CompareMatrixPricing";
import {
  CompareMatrixPricingData,
  CompareMatrixBannerType,
} from "../../types/CompareMatrixPricing";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { navigate } from "gatsby";
import { Builder } from "@builder.io/react";
import SPfetch from "@services/SPFetch.class";
import { Cookie } from "../../../../utils/Cookie";
import BuilderStringInput from "../../../../utils/BuilderInputs/BuilderStringInput";

const CompareMatrixPricingWrapper: React.FC<CompareMatrixBannerType> = ({
  includeBuyNowToggle,
  buyNowInfo,
  pricingDataEntry,
}) => {
  const onClickHandler: React.MouseEventHandler<HTMLAnchorElement> = e => {
    const promo = new URLSearchParams(new URL(e.currentTarget.href).search);
    const promoValue = promo.get("p");
    if (promoValue) {
      Cookie.setCookie("PROMOCODE", promoValue, 365);
    }

    const determineRouterUrl =
      new URL(document.baseURI).origin ===
      new URL(e.currentTarget.href, document.baseURI).origin;
    if (determineRouterUrl) {
      e.preventDefault();
      navigate(e.currentTarget.href);
    }

    window?.analytics.track("Marketing CTA Clicked", {
      url: window.location.pathname,
      text: e.currentTarget.textContent,
      target: determineRouterUrl
        ? e.currentTarget.pathname
        : e.currentTarget.href,
      initiator: "User",
      object: "Marketing CTA",
      action: "Clicked",
      source_application: "Marketing",
    });
  };

  const [data, setData] = useState<CompareMatrixPricingData>();
  const banner = { includeBuyNowToggle, buyNowInfo, onClickHandler };

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      const pricingDataQuery =
        pricingDataEntry === "Entry 1"
          ? "&query.name=Pricing-Entry-1"
          : pricingDataEntry === "Entry 2"
          ? "&query.name=Pricing-Entry-2"
          : "";

      const data = await SPfetch.get(
        `https://cdn.builder.io/api/v3/content/pricing?apiKey=${process.env.GATSBY_BUILDER_REBRAND_API_KEY}${pricingDataQuery}`
      );

      if (!isCancelled) {
        setData(data?.results[0]?.data);
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, []);

  if (data) {
    return data && <CompareMatrixPricing pricingData={data} banner={banner} />;
  } else {
    return (
      <div className="flex justify-center items-center">
        <div className="max-w-desktop w-full my-0 mx-10 sm:mx-5 pb-12">
          {includeBuyNowToggle && (
            <div className="sm:my-md bg-sun-200 rounded-sm p-sm my-12 relative">
              <SkeletonTheme baseColor="var(--sun-100)" highlightColor="#fff">
                <Skeleton
                  count={1}
                  width="65%"
                  height="28px"
                  className="md:mb-3 mb-xxxs"
                />
                <Skeleton count={1} width="65%" className="mb-xs" />
                <Skeleton
                  count={1}
                  width="100px"
                  className="!absolute bottom-[30px] md:!relative md:bottom-[0] md:mb-[20px]"
                />
                <Skeleton
                  count={1}
                  width="25%"
                  height="50px"
                  className="!absolute md:right-0 md:left-0 md:!relative md:top-0 right-[42px] top-[calc(50%-25px)]"
                />
              </SkeletonTheme>
            </div>
          )}
          <div
            className={`flex gap-xs sm:block ${
              !includeBuyNowToggle ? "mt-[90px] md:mt-[55px]" : ""
            }`}
          >
            <Skeleton
              count={1}
              height="40px"
              containerClassName="flex-1 w-[28%]"
              className="sm:mb-xxs"
            />
            <Skeleton
              count={1}
              height="50vh"
              containerClassName="flex-1 w-[24%]"
              className="sm:!h-[80px] sm:!rounded-circle sm:mb-7"
            />
            <Skeleton
              count={1}
              height="50vh"
              containerClassName="flex-1 w-[24%]"
              className="sm:!h-[60px] sm:mb-xxxs"
            />
            <Skeleton
              count={1}
              height="50vh"
              containerClassName="flex-1 w-[24%]"
              className="sm:!h-[200px]"
            />
          </div>
          <div className="flex gap-xs mt-sm sm:none">
            <Skeleton
              count={1}
              height="40px"
              containerClassName="flex-1 w-[28%]"
              className="sm:mb-xxs"
            />
            <Skeleton
              count={1}
              height="50vh"
              containerClassName="flex-1 w-[24%]"
              className="sm:!h-[80px] sm:!rounded-circle sm:mb-7"
            />
            <Skeleton
              count={1}
              height="50vh"
              containerClassName="flex-1 w-[24%]"
              className="sm:!h-[60px] sm:mb-xxxs"
            />
            <Skeleton
              count={1}
              height="50vh"
              containerClassName="flex-1 w-[24%]"
              className="sm:!h-[200px]"
            />
          </div>
        </div>
      </div>
    );
  }
};

export default CompareMatrixPricingWrapper;

export const RegisterCompareMatrixPricingWrapper = () => {
  Builder.registerComponent(CompareMatrixPricingWrapper, {
    name: "compare-matrix-pricing",
    inputs: [
      {
        name: "includeBuyNowToggle",
        type: "boolean",
      },
      new BuilderStringInput("pricingDataEntry", {
        defaultValue: "Entry 1",
        enum: ["Entry 1", "Entry 2"],
      }),
      {
        name: "buyNowInfo",
        type: "object",
        showIf: `options.get('includeBuyNowToggle') === true`,
        subFields: [
          {
            name: "headline",
            type: "string",
          },
          {
            name: "buyNow",
            type: "object",
            subFields: [
              {
                name: "toggleCopy",
                type: "string",
              },
              {
                name: "discountPercent",
                type: "number",
              },
              {
                name: "anchorLink",
                type: "string",
                helperText:
                  "Add this to the URL this component lives on to show this as the selected offer on load.",
              },
              {
                name: "subCopy",
                type: "longText",
              },
              {
                name: "ctaCopy",
                type: "string",
              },
              {
                name: "ctaLink",
                type: "reference",
                model: "page",
              },
              {
                name: "ctaUrl",
                type: "link",
              },
            ],
          },
          {
            name: "_30DayTrial",
            type: "object",
            subFields: [
              {
                name: "toggleCopy",
                type: "string",
              },
              {
                name: "anchorLink",
                type: "string",
                helperText:
                  "Add this to the URL this component lives on to show this as the selected offer on load.",
              },
              {
                name: "subCopy",
                type: "longText",
              },
              {
                name: "ctaCopy",
                type: "string",
              },
              {
                name: "ctaLink",
                type: "reference",
                model: "page",
              },
              {
                name: "ctaUrl",
                type: "link",
              },
            ],
          },
        ],
      },
    ],
  });
};
